import React, { useEffect, useState, useRef } from "react";
import Footer from "../../Components/footer/Footer";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import service from "../../services/service";
import endpoints from "../../services/endpoints";
import "../../common.css";
import {
  GoogleMap,
  Marker,
  Polyline,
  useJsApiLoader,
  OverlayView,
} from "@react-google-maps/api";

import polyline from "@mapbox/polyline";

const ROUTE_CONNECTOR_PREFIX = "__ROUTE_CONNECTOR__";
const GREEN_MILESTONE_ICON =
  "http://maps.google.com/mapfiles/ms/icons/green-dot.png";
const RED_CHECKPOINT_ICON =
  "http://maps.google.com/mapfiles/ms/icons/red-dot.png";
const ROUTE_STROKE = {
  strokeColor: "#ff3b30",
  strokeWeight: 5,
};

const isSameLatLng = (a, b, epsilon = 0.00001) =>
  a &&
  b &&
  Number.isFinite(a.lat) &&
  Number.isFinite(a.lng) &&
  Number.isFinite(b.lat) &&
  Number.isFinite(b.lng) &&
  Math.abs(a.lat - b.lat) < epsilon &&
  Math.abs(a.lng - b.lng) < epsilon;

const toLatLng = (point) => {
  const lat = Number(point?.latitude ?? point?.lat);
  const lng = Number(point?.longitude ?? point?.lng);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return { lat, lng };
};

const isConnectorPoint = (point) =>
  String(point?.title || "").startsWith(ROUTE_CONNECTOR_PREFIX);

/** straight line when mode is straight/null/missing/manual */
const isStraightRouteMode = (point) => {
  if (!point) return true;
  if (point.routeSource === "MANUAL") return true;

  const mode = point.routeMode;
  if (mode == null || mode === "") return true;

  return String(mode).toLowerCase() === "straight";
};

const squaredDistance = (a, b) => {
  const dLat = a.lat - b.lat;
  const dLng = a.lng - b.lng;
  return dLat * dLat + dLng * dLng;
};

const findClosestPolylineIndex = (path, target, fromIndex = 0) => {
  let bestIndex = Math.min(fromIndex, Math.max(path.length - 1, 0));
  let bestDist = Infinity;

  for (let i = fromIndex; i < path.length; i++) {
    const dist = squaredDistance(path[i], target);
    if (dist < bestDist) {
      bestDist = dist;
      bestIndex = i;
    }
  }

  return bestIndex;
};


const buildRouteSegments = (sortedRoutePoints, decodedPolyline) => {
  if (sortedRoutePoints.length < 2) {
    if (decodedPolyline.length > 1) {
      return [{ id: "fallback-polyline", path: decodedPolyline }];
    }
    return [];
  }

  const segments = [];
  let searchStart = 0;

  for (let i = 0; i < sortedRoutePoints.length - 1; i++) {
    const fromPoint = sortedRoutePoints[i];
    const toPoint = sortedRoutePoints[i + 1];
    const from = toLatLng(fromPoint);
    const to = toLatLng(toPoint);
    if (!from || !to) continue;

    const useStraight = isStraightRouteMode(toPoint);
    let path;

    if (!useStraight && decodedPolyline.length > 1) {
      const fromIdx = findClosestPolylineIndex(decodedPolyline, from, searchStart);
      const toIdx = findClosestPolylineIndex(decodedPolyline, to, fromIdx);
      searchStart = toIdx;

      const legPath = decodedPolyline.slice(fromIdx, toIdx + 1);
      path = legPath.length >= 2 ? legPath : [from, to];
    } else {
      path = [from, to];

      if (decodedPolyline.length > 1) {
        searchStart = findClosestPolylineIndex(decodedPolyline, to, searchStart);
      }
    }

    segments.push({
      id: `segment-${fromPoint.id ?? i}-${toPoint.id ?? i + 1}`,
      path,
    });
  }

  return segments;
};

const LocationMap = ({
  startLat,
  startLng,
  endLat,
  endLng,
  startPoint,
  endPoint,
  milestones = [],
  routePoints = [],
  routePolyline,
}) => {
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
  });

  const sortedRoutePoints = Array.isArray(routePoints)
    ? [...routePoints]
        .filter((point) => toLatLng(point))
        .sort(
          (a, b) =>
            (Number(a.pointOrder) || 0) - (Number(b.pointOrder) || 0),
        )
    : [];

  const decodedPolyline = routePolyline
    ? polyline.decode(routePolyline).map(([lat, lng]) => ({
        lat,
        lng,
      }))
    : [];

  const routeSegments = buildRouteSegments(sortedRoutePoints, decodedPolyline);

  const boundsPoints = routeSegments.flatMap((segment) => segment.path);

  const startPosition =
    Number.isFinite(Number(startLat)) && Number.isFinite(Number(startLng))
      ? { lat: Number(startLat), lng: Number(startLng) }
      : toLatLng(sortedRoutePoints[0]) || boundsPoints[0] || null;

  const endPosition =
    Number.isFinite(Number(endLat)) && Number.isFinite(Number(endLng))
      ? { lat: Number(endLat), lng: Number(endLng) }
      : toLatLng(sortedRoutePoints[sortedRoutePoints.length - 1]) ||
        boundsPoints[boundsPoints.length - 1] ||
        null;

  // Fallback when there are no routePoints: single polyline or start→end
  const fallbackPath =
    routeSegments.length > 0
      ? []
      : decodedPolyline.length > 1
        ? decodedPolyline
        : startPosition && endPosition
          ? [startPosition, endPosition]
          : [];

  const hasMapData =
    routeSegments.length > 0 ||
    fallbackPath.length > 0 ||
    (startPosition && endPosition && startPoint && endPoint);

  if (!hasMapData) {
    return null;
  }

  const center =
    boundsPoints[0] || fallbackPath[0] || startPosition;

  const handleMapLoad = (map) => {
    const pointsForBounds =
      boundsPoints.length > 0
        ? boundsPoints
        : fallbackPath.length > 0
          ? fallbackPath
          : [startPosition, endPosition].filter(Boolean);

    if (pointsForBounds.length > 0) {
      const bounds = new window.google.maps.LatLngBounds();
      pointsForBounds.forEach((point) => bounds.extend(point));
      map.fitBounds(bounds);
    }
  };

  const getMilestonePosition = (milestone) => {
    const ownPosition = toLatLng(milestone);
    if (!sortedRoutePoints.length) return ownPosition;

    if (milestone?.title) {
      const byTitle = sortedRoutePoints.find(
        (point) =>
          point.title &&
          !isConnectorPoint(point) &&
          point.title === milestone.title,
      );
      const titlePosition = toLatLng(byTitle);
      if (titlePosition) return titlePosition;
    }

    if (ownPosition) {
      let nearest = null;
      let nearestDistance = Infinity;

      sortedRoutePoints.forEach((point) => {
        if (isConnectorPoint(point)) return;
        const pointPosition = toLatLng(point);
        if (!pointPosition) return;

        const distance =
          Math.abs(pointPosition.lat - ownPosition.lat) +
          Math.abs(pointPosition.lng - ownPosition.lng);

        if (distance < nearestDistance) {
          nearestDistance = distance;
          nearest = pointPosition;
        }
      });

      if (nearest && nearestDistance < 0.00001) {
        return nearest;
      }
    }

    return ownPosition;
  };

  const milestonePositions = (Array.isArray(milestones) ? milestones : [])
    .map((item) => ({
      item,
      position: getMilestonePosition(item),
    }))
    .filter(({ position }) => position);

  // Checkpoints: visible route points (hide connectors; skip start/end & milestone pins)
  const checkpointMarkers = sortedRoutePoints.filter((point) => {
    if (isConnectorPoint(point)) return false;

    const position = toLatLng(point);
    if (!position) return false;

    if (startPosition && isSameLatLng(position, startPosition)) return false;
    if (endPosition && isSameLatLng(position, endPosition)) return false;

    const matchesMilestone = milestonePositions.some(({ position: milestonePos }) =>
      isSameLatLng(position, milestonePos),
    );
    if (matchesMilestone) return false;

    return true;
  });

  if (!isLoaded) {
    return <div>Loading Map...</div>;
  }

  const startEndOverlayStyle = {
    transform: "translate(-50%, -65px)",
    background: "#fff",
    border: "2px solid #ef4444",
    borderRadius: "8px",
    padding: "8px 12px",
    fontSize: "13px",
    fontWeight: "600",
    color: "#222",
    boxShadow: "0 3px 10px rgba(0,0,0,.25)",
    width: "100px",
    textAlign: "center",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  };

  const milestoneOverlayStyle = {
    ...startEndOverlayStyle,
    border: "2px solid #22c55e",
  };

  return (
    <GoogleMap
      mapContainerStyle={{
        width: "100%",
        height: "100%",
      }}
      center={center}
      zoom={8}
      onLoad={handleMapLoad}
      options={{
        streetViewControl: true,
        mapTypeControl: true,
        fullscreenControl: true,
      }}
    >
      {/* Path segments from routePoints + routeMode */}
      {routeSegments.map((segment) =>
        segment.path.length > 1 ? (
          <Polyline
            key={segment.id}
            path={segment.path}
            options={ROUTE_STROKE}
          />
        ) : null,
      )}

      {fallbackPath.length > 1 && (
        <Polyline path={fallbackPath} options={ROUTE_STROKE} />
      )}

      {/* Checkpoint / route points — RED */}
      {checkpointMarkers.map((point) => {
        const position = toLatLng(point);
        return (
          <Marker
            key={
              point.id ??
              `route-${point.pointOrder}-${position.lat}-${position.lng}`
            }
            position={position}
            icon={RED_CHECKPOINT_ICON}
            title={point.title || "Checkpoint"}
          />
        );
      })}

      {/* Start Point */}
      {startPosition && (
        <>
          <Marker position={startPosition} />
          {startPoint && (
            <OverlayView
              position={startPosition}
              mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
            >
              <div style={startEndOverlayStyle}>{startPoint}</div>
            </OverlayView>
          )}
        </>
      )}

      {/* End Point */}
      {endPosition && (
        <>
          <Marker position={endPosition} />
          {endPoint && (
            <OverlayView
              position={endPosition}
              mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
            >
              <div style={startEndOverlayStyle}>{endPoint}</div>
            </OverlayView>
          )}
        </>
      )}

      {/* Milestones — GREEN */}
      {milestonePositions.map(({ item, position }) => (
        <React.Fragment key={item.id ?? `${position.lat}-${position.lng}`}>
          <Marker position={position} icon={GREEN_MILESTONE_ICON} />
          <OverlayView
            position={position}
            mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
          >
            <div style={milestoneOverlayStyle}>{item.title}</div>
          </OverlayView>
        </React.Fragment>
      ))}
    </GoogleMap>
  );
};

export default function ChallengeDetails() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const [challengeData, setChallengeData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isFavourite, setIsFavourite] = useState(false);
  const [milestoneModal, setMilestoneModal] = useState(false);
  const [paymentModal, setPaymentModal] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [purchaseModal, setPurchaseModal] = useState(false);
  const [teamAction, setTeamAction] = useState("");
  const navigate = useNavigate();

  // Coupon configuration states
  const [couponInput, setCouponInput] = useState("");
  const [appliedCouponCode, setAppliedCouponCode] = useState("");
  const [discountedPrice, setDiscountedPrice] = useState(null);
  const [couponLoading, setCouponLoading] = useState(false);

  // Reusable Notification Modal State (Replaces alerts)
  const [notificationModal, setNotificationModal] = useState({
    show: false,
    title: "",
    message: "",
    type: "success", // 'success' | 'error' | 'warning'
  });

  // Marketing Campaign configurations
  const [campaigns, setCampaigns] = useState([]);
  const [showCampaignDropdown, setShowCampaignDropdown] = useState(false);
  const dropdownRef = useRef(null);

  const triggerNotification = (title, message, type = "success") => {
    setNotificationModal({
      show: true,
      title,
      message,
      type,
    });
  };

  const fetchChallengeDetails = async () => {
    try {
      setLoading(true);
      const response = await service.get(
        endpoints.challenges.challengeDetails(id),
      );
      setChallengeData(response?.data?.data);
    } catch (error) {
      console.log("Challenge Details Error", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFavouriteStatus = async (challengeId) => {
    try {
      const response = await service.get(
        endpoints.challenges.getFavouriteStatus(challengeId),
      );
      setIsFavourite(response?.data?.data?.isFavourite);
    } catch (error) {
      console.log("Favourite Status Error", error);
    }
  };

  const fetchMarketingCampaigns = async () => {
    try {
      const response = await service.get(endpoints.marketingCampaign.getCampaigns);
      const availableCampaigns = response?.data?.data || response?.data || [];
      setCampaigns(Array.isArray(availableCampaigns) ? availableCampaigns : []);
    } catch (error) {
      console.error("Error fetching marketing campaigns:", error);
    }
  };

  /* FAVOURITE CHALLENGE */
  const handleFavourite = async () => {
    try {
      const newValue = !isFavourite;
      setIsFavourite(newValue);

      await service.post(endpoints.challenges.favouriteChallenge, {
        challengeId: challengeData?.id,
        isFavourite: newValue,
      });
    } catch (error) {
      console.log("Favourite Error", error);
      setIsFavourite(!isFavourite);
    }
  };

  useEffect(() => {
    if (id) {
      fetchChallengeDetails();
      fetchFavouriteStatus(id);
      fetchMarketingCampaigns();
    }
  }, [id]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowCampaignDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (id) {
      const savedDiscount = localStorage.getItem(`discount_${id}`);
      const savedCode = localStorage.getItem(`couponCode_${id}`);
      if (savedDiscount) {
        setDiscountedPrice(savedDiscount);
      }
      if (savedCode) {
        setAppliedCouponCode(savedCode);
      }
    }
  }, [id]);

  useEffect(() => {
    const status = searchParams.get("payment");

    if (status === "success") {
      setPaymentStatus("success");
      setPaymentModal(true);
    }

    if (status === "failed" || status === "cancel") {
      setPaymentStatus("failed");
      setPaymentModal(true);
    }
  }, [searchParams]);

  const handleApplyCoupon = async () => {
    if (!couponInput.trim()) {
      triggerNotification("Missing Information", "Please enter a coupon code", "warning");
      return;
    }

    try {
      setCouponLoading(true);
      const response = await service.post(
        endpoints.challengeOrders.applyCoupon,
        {
          challengeId: parseInt(id || challengeData?.id),
          couponCode: couponInput.trim(),
        }
      );

      const newPrice = response?.data?.data?.payableAmount;

      if (newPrice !== undefined && newPrice !== null) {
        setDiscountedPrice(newPrice);
        setAppliedCouponCode(couponInput.trim());
        
        localStorage.setItem(`discount_${id}`, newPrice);
        localStorage.setItem(`couponCode_${id}`, couponInput.trim());
        
        triggerNotification("Success!", "Coupon applied successfully!", "success");
      } else {
        triggerNotification("Coupon Applied", "Coupon processed, but no price alteration returned.", "warning");
      }
    } catch (error) {
      console.error("Apply Coupon Error:", error);
      const errMsg = error?.response?.data?.message || "Invalid Coupon Code";
      triggerNotification("Application Failed", errMsg, "error");
    } finally {
      setCouponLoading(false);
    }
  };

  const handleSelectCoupon = (couponCode) => {
    setCouponInput(couponCode);
    setShowCampaignDropdown(false);
  };

  const handleJoinIndividual = async () => {
    try {
      const requestPayload = {
        challengeId: parseInt(challengeData?.id),
      };

      const localCode = localStorage.getItem(`couponCode_${id}`);
      if (appliedCouponCode || localCode) {
        requestPayload.couponCode = appliedCouponCode || localCode;
      }

      const orderResponse = await service.post(
        endpoints.payment_order.createOrder,
        requestPayload
      );

      const orderId = orderResponse?.data?.data?.orderId;

      if (!orderId) {
        throw new Error("Order ID not found");
      }

      const checkoutResponse = await service.post(
        endpoints.payment.createCheckout(orderId),
      );
      const checkoutUrl = checkoutResponse?.data?.data?.checkoutUrl;

      if (checkoutUrl) {
        window.location.href = checkoutUrl;
      } else {
        console.error("Checkout URL not found in response");
      }
    } catch (error) {
      console.error("Join Individual Error:", error);
    }
  };

  const handleTeamAction = async (actionType) => {
    try {
      const response = await service.get(
        endpoints.challengeOrders.challengeStatus(challengeData?.id),
      );

      const isPurchased = response?.data?.data?.isPurchased;

      if (isPurchased) {
        if (actionType === "create") {
          navigate("/team-management/create-group");
        } else {
          navigate(`/team-management/challenge/${challengeData?.id}`);
        }
        return;
      }

      setTeamAction(actionType);
      setPurchaseModal(true);
    } catch (error) {
      console.error("Challenge Status Error:", error);
    }
  };

  const closePaymentModal = () => {
    setPaymentModal(false);

    localStorage.removeItem(`discount_${id}`);
    localStorage.removeItem(`couponCode_${id}`);
    setDiscountedPrice(null);
    setAppliedCouponCode("");
    setCouponInput("");

    window.history.replaceState({}, document.title, window.location.pathname);
  };

  if (loading) {
    return <div className="loading-text">Loading challenge details...</div>;
  }

  return (
    <div className="challenge-page">
      <div className="challenge-container">
        {/* LEFT SECTION */}
        <div className="challenge-left">
          <button
            className="favorite-btn"
            onClick={handleFavourite}
            style={{
              color: isFavourite ? "red" : "#FFD700",
              fontSize: "28px",
            }}
          >
            ★
          </button>
          <h1 className="challenge-title">{challengeData?.challengeName}</h1>
          <p className="challenge-subtitle">{challengeData?.description}</p>
          
          <div className="stats-row">
            <div className="stat-card">
              <h3>{challengeData?.totalDistance} KM</h3>
              <span>DISTANCE</span>
            </div>

            <div className="stat-card">
              <h3>{challengeData?.totalMilestoneCount}</h3>
              <span>MILESTONES</span>
            </div>

            <div className="stat-card">
              <h3>
                {Math.floor(challengeData?.timeInMinutes / 60)}H{" "}
                {challengeData?.timeInMinutes % 60}M
              </h3>
              <span>DURATION</span>
            </div>
          </div>

          <h3 className="section-title">Challenge Overview</h3>
          <p className="description">{challengeData?.challengeOverview}</p>
          
          <h3 className="section-title">Rewards</h3>
          <div className="reward-row">
            <div className="reward-card">
              <span className="flag">🚩</span>
              <h4>{challengeData?.rewardPoints}</h4>
              <p>REWARD POINTS</p>
            </div>

            <div
              className="reward-card"
              style={{ cursor: "pointer" }}
              onClick={() => setMilestoneModal(true)}
            >
              <span className="flag">🚩</span>
              <h4>{challengeData?.totalMilestoneCount}</h4>
              <p>MILESTONES</p>
            </div>

            <div className="reward-card">
              <span className="flag">🚩</span>
              <h4>{challengeData?.participants}</h4>
              <p>PARTICIPANTS</p>
            </div>
          </div>

          {milestoneModal && (
            <div
              className="milestone-overlay"
              onClick={() => setMilestoneModal(false)}
            >
              <div
                className="milestone-modal"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="milestone-modal-header">
                  <div className="milestone-header-left">
                    <span className="milestone-header-icon"></span>
                    <h2>Milestone Details</h2>
                  </div>
                  <button
                    className="milestone-close-btn"
                    onClick={() => setMilestoneModal(false)}
                  >
                    ✕
                  </button>
                </div>

                <div className="milestone-body">
                  {challengeData?.routeSegments?.map((segment, index) => (
                    <div key={index} className="milestone-item">
                      <div className="milestone-item-top">
                        <div className="milestone-point-icon">
                          {index === 0 ? "🚩" : "📍"}
                        </div>
                        <div className="milestone-point-info">
                          <p className="milestone-point-label">
                            {index === 0 ? "START POINT" : `MILESTONE ${index}`}
                          </p>
                          <h4 className="milestone-point-name">
                            {segment.from}
                          </h4>
                        </div>
                        <div className="milestone-point-info milestone-point-right">
                          <p className="milestone-point-label red">
                            {index === challengeData.routeSegments.length - 1
                              ? "END POINT"
                              : `MILESTONE ${index + 1}`}
                          </p>
                          <h4 className="milestone-point-name red">
                            {segment.to}
                          </h4>
                        </div>
                      </div>
                      <div className="milestone-distance-bar">
                        <span className="milestone-distance-icon">🏃</span>
                        <span>{segment.distance} KM</span>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="milestone-total">
                  <span className="milestone-total-icon"></span>
                  <span>Total Distance</span>
                  <span className="milestone-total-km">
                    {challengeData?.totalDistance} KM
                  </span>
                </div>
              </div>
            </div>
          )}

          <h3 className="section-title">How to get rewards?</h3>
          <p className="description">
            Complete the challenge and cross all milestones to earn reward
            points and certificates.
          </p>
        </div>

        {/* RIGHT SECTION */}
        <div className="challenge-right">
          <div className="image-card">
            <img src={challengeData?.thumbnailImage} alt="challenge" />
          </div>

          <div className="option-card">
            <h3>Participation Options</h3>

            <button className="outline-btn" onClick={handleJoinIndividual}>
              JOIN AS INDIVIDUAL @{" "}
              {discountedPrice !== null ? (
                <>
                  <span style={{ textDecoration: "line-through", marginRight: "8px", color: "#888" }}>
                    {challengeData?.challengePrice}
                  </span>
                  <span style={{ color: "#2ed573", fontWeight: "bold" }}>
                    {discountedPrice}
                  </span>
                </>
              ) : (
                challengeData?.challengePrice
              )}
            </button>

            <button
              className="outline-btn"
              onClick={() => handleTeamAction("create")}
            >
              CREATE TEAM
            </button>

            <button
              className="outline-btn"
              onClick={() => handleTeamAction("join")}
            >
              JOIN EXISTING TEAM
            </button>
          </div>

          <div className="coupon-card" style={{ position: "relative" }} ref={dropdownRef}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <h3>Coupon Code</h3>
              <button 
                type="button"
                onClick={() => setShowCampaignDropdown(!showCampaignDropdown)}
                style={{
                  background: "none",
                  border: "none",
                  color: "#3498db",
                  cursor: "pointer",
                  fontSize: "13px",
                  fontWeight: "600",
                  padding: "0"
                }}
              >
                (Offer available)
              </button>
            </div>

            <div className="coupon-box">
              <input 
                type="text" 
                placeholder="Enter coupon code" 
                value={couponInput}
                onChange={(e) => setCouponInput(e.target.value)}
              />

              <button onClick={handleApplyCoupon} disabled={couponLoading}>
                {couponLoading ? "..." : "APPLY"}
              </button>
            </div>

            {showCampaignDropdown && (
              <div style={{
                position: "absolute",
                top: "100%",
                left: "0",
                right: "0",
                backgroundColor: "#fff",
                border: "1px solid #e0e0e0",
                borderRadius: "8px",
                boxShadow: "0px 4px 12px rgba(0,0,0,0.15)",
                zIndex: "999",
                maxHeight: "220px",
                overflowY: "auto",
                marginTop: "6px",
                padding: "8px"
              }}>
                {campaigns.length === 0 ? (
                  <p style={{ margin: "8px", fontSize: "13px", color: "#888", textAlign: "center" }}>
                    No special promotions available currently.
                  </p>
                ) : (
                  campaigns.map((camp) => (
                    <div 
                      key={camp.id || camp.couponCode}
                      onClick={() => handleSelectCoupon(camp.couponCode || camp.code)}
                      style={{
                        padding: "10px",
                        cursor: "pointer",
                        borderBottom: "1px solid #f5f5f5",
                        transition: "background-color 0.2s"
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#f9f9f9"}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "transparent"}
                    >
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px" }}>
                        <span style={{ fontWeight: "bold", color: "#ff3b30", fontSize: "14px" }}>
                          {camp.couponCode || camp.code}
                        </span>
                        {camp.discountValue && (
                          <span style={{ fontSize: "12px", color: "#2ed573", fontWeight: "600" }}>
                            {camp.discountValue}% OFF
                          </span>
                        )}
                      </div>
                      <p style={{ fontSize: "12px", color: "#666", margin: "0", lineHeight: "1.4" }}>
                        {camp.description || "Apply to get special price alteration."}
                      </p>
                    </div>
                  ))
                )}
              </div>
            )}

            {appliedCouponCode && (
              <p style={{ color: "#2ed573", fontSize: "13px", marginTop: "5px", paddingLeft: "5px" }}>
                Code "{appliedCouponCode}" is active!
              </p>
            )}
          </div>
        </div>
      </div>

      {/* LOCATION MAP */}
      <div className="location-section">
        <h2 style={{ marginLeft: "9vw" }} className="section-heading">
          Location Map
        </h2>

        <div className="map-container">
          <LocationMap
            startLat={challengeData?.startPointLatitude}
            startLng={challengeData?.startPointLongitude}
            endLat={challengeData?.endPointLatitude}
            endLng={challengeData?.endPointLongitude}
            startPoint={challengeData?.startPoint}
            endPoint={challengeData?.endPoint}
            milestones={challengeData?.milestoneDetails}
            routePoints={challengeData?.routePoints}
            routePolyline={challengeData?.routePolyline}
          />
        </div>
      </div>

      {/* BENEFITS */}
      <div className="benefits-section">
        <h2 className="section-heading">Benefits</h2>

        <div className="benefits-grid">
          <div className="benefit-card">
            <span className="benefit-flag">🚩</span>
            <h3>{challengeData?.rewardPoints}</h3>
            <p>Reward Points</p>
          </div>

          <div className="benefit-card">
            <span className="benefit-flag">🚩</span>
            <h3>{challengeData?.totalMilestoneCount}</h3>
            <p>Milestones</p>
          </div>

          <div className="benefit-card">
            <span className="benefit-flag">🚩</span>
            <h3>{challengeData?.totalDistance}</h3>
            <p>Total Distance</p>
          </div>

          <div className="benefit-card">
            <span className="benefit-flag">🚩</span>
            <h3>{challengeData?.participants}</h3>
            <p>Participants</p>
          </div>
        </div>
      </div>

      {/* RECENT FINISHERS */}
      <div className="finishers-section">
        <h2 className="section-heading">Recent Finishers</h2>

        <div className="finishers-grid">
          {challengeData?.recentFinisher?.length > 0 ? (
            challengeData?.recentFinisher?.map((user, index) => (
              <div className="finisher-card" key={index}>
                <img
                  src={
                    user?.profileImage ||
                    `https://picsum.photos/80/80?random=${index}`
                  }
                  alt=""
                />

                <div className="finisher-info">
                  <h4>{user?.participantName}</h4>
                  <p>Rank #{user?.rank}</p>
                </div>

                <span className="finisher-rank">
                  #{String(user?.rank).padStart(2, "0")}
                </span>
              </div>
            ))
          ) : (
            <p>No finishers yet</p>
          )}
        </div>
      </div>

      {/* PAYMENT TRANSACTION OVERLAY MODAL */}
      {paymentModal && (
        <div className="payment-modal-overlay" onClick={closePaymentModal}>
          <div
            className={`payment-modal-card ${
              paymentStatus === "success" ? "payment-success" : "payment-failed"
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="payment-icon">
              {paymentStatus === "success" ? "✓" : "✕"}
            </div>

            <h2>
              {paymentStatus === "success"
                ? "Payment Successful"
                : "Payment Failed"}
            </h2>

            <p>
              {paymentStatus === "success"
                ? "Your transaction has been completed successfully."
                : "Your transaction could not be processed."}
            </p>

            <div className="payment-info">
              <span>Amount</span>
              <strong>
                {discountedPrice !== null ? discountedPrice : (challengeData?.challengePrice || 0)}
              </strong>
            </div>

            <button
              className={`payment-btn ${
                paymentStatus === "success"
                  ? "payment-btn-success"
                  : "payment-btn-failed"
              }`}
              onClick={closePaymentModal}
            >
              {paymentStatus === "success" ? "Done" : "Try Again"}
            </button>
          </div>
        </div>
      )}

      {/* OOPS / PURCHASE REQUIREMENT MODAL */}
      {purchaseModal && (
        <div
          className="payment-modal-overlay"
          onClick={() => setPurchaseModal(false)}
        >
          <div
            className="purchase-modal-card"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="purchase-close-btn"
              onClick={() => setPurchaseModal(false)}
            >
              ✕
            </button>

            <div className="purchase-icon">😅</div>

            <h2>Oops!</h2>

            <h3>
              {teamAction === "create"
                ? "Create a team"
                : "Join an existing team"}
            </h3>

            <p>
              To{" "}
              {teamAction === "create"
                ? "create a team"
                : "join an existing team"}
              , you need to purchase this challenge first.
            </p>

            <button
              className="purchase-btn"
              onClick={() => {
                setPurchaseModal(false);
                handleJoinIndividual();
              }}
            >
              Purchase this challenge
            </button>

            <button
              className="purchase-cancel-btn"
              onClick={() => setPurchaseModal(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* DYNAMIC NOTIFICATION MODAL (REPLACES BROWSER ALERTS) */}
      {notificationModal.show && (
        <div 
          className="app-notification-overlay" 
          onClick={() => setNotificationModal(prev => ({ ...prev, show: false }))}
        >
          <div 
            className={`app-notification-card notify-${notificationModal.type}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="notify-header">
              <h3>{notificationModal.title}</h3>
              <button 
                className="notify-close-x"
                onClick={() => setNotificationModal(prev => ({ ...prev, show: false }))}
              >
                ✕
              </button>
            </div>
            <div className="notify-body">
              <p>{notificationModal.message}</p>
            </div>
            <div className="notify-footer">
              <button 
                className="notify-action-btn"
                onClick={() => setNotificationModal(prev => ({ ...prev, show: false }))}
              >
                Okay
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}